import * as React from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  ActionIcon,
  Group,
  useMantineTheme,
  Flex,
  Stack,
  Divider,
} from "@mantine/core";
import dayjs from "dayjs";

import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { IconPlus, IconTrash, IconX } from "@tabler/icons-react";

import { UserSchema, UserType } from "@supportinitiative/common";

import {
  CSelect,
  CTextInput,
  CDatePicker,
} from "@supportinitiative/react-hook-form-mantine";

import { styles } from "../styles";
import { generateApiKey, postUser } from "../api";
import { AuditType, IDispatchAction } from "../types";
import { getActionOptions } from "./UserUtils";
import { UserContext } from "../misc/UserContext";
import { useActiveRoles, useUser } from "../hooks";
import FormSubmitError from "../ui/Error/FormSubmitError";
import FormStateError from "../ui/Error/FormStateError";
import Success from "../ui/Success";
import Audit from "../ui/Audit/Audit";
import { getId, showError } from "../utilities";

type actionTypes =
  | "CURRENT-ACTION"
  | "ACTION-OPTIONS"
  | "SET-VALUE"
  | "SET-SUBMITTING"
  | "SET-SUCCESS"
  | "SET-ERROR";

export const UserCVMContext = React.createContext<{
  state: any;
  dispatch: React.Dispatch<IDispatchAction<actionTypes>>;
}>({
  state: {},
  dispatch: () => {},
});

const initialState = {
  currentAction: "NEW",
  entityId: "",
  actionOptions: {},
  setValue: () => {},
  submitting: false,
  error: "",
  successMessage: "",
};

function reducer(state: any, action: IDispatchAction<actionTypes>) {
  switch (action.type) {
    case "CURRENT-ACTION":
      return { ...state, currentAction: action.value };
    case "ACTION-OPTIONS":
      return { ...state, actionOptions: action.value };
    case "SET-VALUE":
      return { ...state, setValue: action.value };
    case "SET-SUBMITTING":
      return { ...state, submitting: action.value };
    case "SET-SUCCESS":
      return { ...state, successMessage: action.value };
    case "SET-ERROR":
      return { ...state, error: action.value };
    default:
      return state;
  }
}

function get30DaysPlus() {
  var date = new Date(); // Now
  date.setDate(date.getDate() + 30);
  return dayjs(date).format("YYYY-MM-DD");
}

export default function UserCVM() {
  // let [dataset, setUser] = React.useState<UserType>();

  let [audit, setAudit] = React.useState<AuditType | undefined>();

  // let [currentAction, setCurrentAction] = React.useState("new");
  let navigate = useNavigate();
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId") as string;
  const { state: applicationState } = React.useContext(UserContext);
  const queryClient = useQueryClient();
  const theme = useMantineTheme();

  const { data: dataset } = useUser(entityId, id);
  // let [newUser, setNewUser] = React.useState<UserType | undefined>();
  const [userFormState, userFormDispatch] = React.useReducer(reducer, {
    ...initialState,
    actionOptions: getActionOptions("new", dataset, dataset, applicationState),
  });
  // console.log("dataset in component", dataset);

  const { data: activeRoles } = useActiveRoles(entityId);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<UserType>({
    defaultValues: {
      userId: "",
      userName: "",
      email: "",
      type: "USER",
      roles: [],
      apiKeys: [],
    },
    criteriaMode: "all",
    resolver: yupResolver(UserSchema),
  });

  const rolesArray = useFieldArray({
    control,
    name: "roles",
    keyName: "__key",
  });

  const apiKeysArray = useFieldArray({
    control,
    name: "apiKeys",
    keyName: "__key",
  });

  /* 
  const columns = useWatch({
    control,
    name: "definition.columns",
  }); */

  // const [columns, setColumns] = React.useState<UserType["definition"]["columns"]>([]);
  const onOk = () => {
    navigate(`/app/users?entityId=${entityId}`);
  };

  React.useEffect(() => {
    if (dataset) {
      userFormDispatch({ type: "CURRENT-ACTION", value: "VIEW" });
      userFormDispatch({
        type: "ACTION-OPTIONS",
        value: getActionOptions("view", dataset, dataset, applicationState),
      });

      // setColumns(dataset.definition.columns || []);

      setAudit({
        status: dataset.status,
        authStatus: dataset.authStatus,
        usableStatus: dataset.usableStatus,
        makerId: dataset.makerId,
        makerDate: dataset.makerDate,
        modificationNo: dataset.modificationNo,
        checkerId: dataset.checkerId,
        checkerDate: dataset.checkerDate,
      });

      reset(dataset);
    }
  }, [dataset]);

  const onSubmit = async (data: any) => {
    try {
      userFormDispatch({
        type: "SET-SUBMITTING",
        value: true,
      });
      userFormDispatch({
        type: "SET-ERROR",
        value: "",
      });
      let payload = {
        ...data,
        entityId,
        metaData: {
          action: userFormState.currentAction,
        },
      };
      await postUser(entityId, payload);
      // setError("Form", "Unable to Submit");
      userFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      queryClient.invalidateQueries(["users", entityId]);
      userFormDispatch({
        type: "SET-SUCCESS",
        value: "User saved successfully",
      });
    } catch (error: any) {
      // console.log("post catch", error);
      // console.log("post catch", error?.response?.data?.message);
      userFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      userFormDispatch({
        type: "SET-ERROR",
        value: error?.response?.data?.message || "Unable to save user",
      });
    }
  };

  return (
    <UserCVMContext.Provider
      value={{ state: userFormState, dispatch: userFormDispatch }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ marginTop: theme.spacing.lg }}
        noValidate
      >
        <div id="header">
          <Grid
            align="flex-end"
            gutter={"xs"}
            mt="xs"
            mb="xs"
            // mb="xs"
            style={{
              display: "flex",
              // borderLeft: `${theme.spacing.xxs} solid ${theme.colors.emerald[7]}`,
            }}
            columns={12}
          >
            <Grid.Col span={3}>
              <Flex align={"end"} gap={theme.spacing.xs}>
                <Stack gap={0}>
                  <Title order={3} c="dark">
                    User
                  </Title>
                </Stack>
              </Flex>
            </Grid.Col>
          </Grid>
        </div>

        <Divider size="sm" mb="xs" color="dark.2" />

        <div id="body">
          <Grid align="flex-start" gutter={"xs"} mb="lg">
            <Grid.Col span={2}>
              <CTextInput
                name="userId"
                control={control}
                label="User ID"
                aria-label="User ID"
                required
                showErrorText={false}
                disabled={userFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <CTextInput
                name="userName"
                control={control}
                label="User Name"
                aria-label="User Name"
                required
                showErrorText={false}
                disabled={userFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <CTextInput
                name="email"
                control={control}
                label="Email"
                aria-label="Email"
                required
                showErrorText={false}
                disabled={userFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <CSelect
                name="type"
                control={control}
                label="Type"
                aria-label="Type"
                data={[
                  { label: "User", value: "USER" },
                  { label: "System", value: "SYSTEM" },
                ]}
                required
                showErrorText={false}
                disabled={userFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <Stack gap="0" mb="lg">
            <Group mb="xs" gap={"xs"} style={{ alignItems: "center" }}>
              <Flex>
                <Title
                  order={5}
                  style={{
                    padding: "0px 5px",
                    backgroundColor: "#16A596",
                    color: "#fff",
                  }}
                >
                  Roles
                </Title>
              </Flex>

              <ActionIcon
                variant="light"
                size="lg"
                onClick={() => {
                  rolesArray.append({
                    role: "",
                  });
                }}
                style={{
                  display:
                    userFormState.currentAction.toUpperCase() === "VIEW"
                      ? "none"
                      : "",
                }}
                disabled={userFormState.actionOptions.disableAll}
              >
                <IconPlus style={{ width: "70%", height: "70%" }} stroke={2} />
              </ActionIcon>
            </Group>

            {rolesArray.fields.map((field, index) => {
              return (
                <Grid align="stretch" gutter={"xs"} mb="xs" key={field.__key}>
                  <Grid.Col span={8}>
                    <CSelect
                      name={`roles.${index}.role`}
                      control={control}
                      data={
                        activeRoles
                          ? activeRoles.map((role) => {
                              return {
                                label: `${role.name} - ${role.description}`,
                                value: role.name,
                              };
                            })
                          : []
                      }
                      showErrorText={false}
                      required
                      disabled={userFormState.actionOptions.disableAll}
                    />
                  </Grid.Col>

                  <Grid.Col
                    span={1}
                    style={{ display: "flex", alignItems: "flex-end" }}
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => {
                        rolesArray.remove(index);
                      }}
                      style={{
                        display:
                          userFormState.currentAction.toUpperCase() === "VIEW"
                            ? "none"
                            : "",
                      }}
                      disabled={userFormState.actionOptions.disableAll}
                    >
                      <IconTrash />
                    </ActionIcon>
                  </Grid.Col>
                </Grid>
              );
            })}
          </Stack>

          <Stack gap="0" mb="lg">
            <Group mb="xs" gap={"xs"} style={{ alignItems: "center" }}>
              <Flex>
                <Title
                  order={5}
                  style={{
                    padding: "0px 5px",
                    backgroundColor: "#16A596",
                    color: "#fff",
                  }}
                >
                  API Keys
                </Title>
              </Flex>

              <ActionIcon
                variant="light"
                size="lg"
                onClick={async () => {
                  try {
                    const response = await generateApiKey(entityId);
                    if (response) {
                      apiKeysArray.append({
                        id: response.id,
                        key: response.key,
                        name: "",
                        expiryDate: get30DaysPlus() as unknown as Date,
                      });
                    }
                  } catch (error: any) {
                    showError(
                      error,
                      "Error in generating api key",
                      "Unable to generate api key",
                      <IconX />
                    );
                  }
                }}
                style={{
                  display:
                    userFormState.currentAction.toUpperCase() === "VIEW"
                      ? "none"
                      : "",
                }}
                disabled={userFormState.actionOptions.disableAll}
              >
                <IconPlus style={{ width: "70%", height: "70%" }} stroke={2} />
              </ActionIcon>
            </Group>

            {apiKeysArray.fields.map((field, index) => {
              return (
                <Grid align="stretch" gutter={"xs"} mb="xs" key={field.key}>
                  <Grid.Col span={2}>
                    <CTextInput
                      name={`apiKeys.${index}.id`}
                      control={control}
                      label={index === 0 ? "ID" : ""}
                      aria-label="ID"
                      showErrorText={false}
                      required
                      disabled={true}
                    />
                  </Grid.Col>
                  <Grid.Col span={2}>
                    <CTextInput
                      name={`apiKeys.${index}.name`}
                      control={control}
                      label={index === 0 ? "Name" : ""}
                      aria-label="Name"
                      showErrorText={false}
                      required
                      disabled={userFormState.actionOptions.disableAll}
                    />
                  </Grid.Col>

                  <Grid.Col span={2}>
                    <CTextInput
                      name={`apiKeys.${index}.key`}
                      control={control}
                      label={index === 0 ? "Key (Hashed on save)" : ""}
                      aria-label="Token"
                      showErrorText={false}
                      required
                      disabled={true}
                    />
                  </Grid.Col>

                  <Grid.Col span={2}>
                    <CDatePicker
                      name={`apiKeys.${index}.expiryDate`}
                      control={control}
                      label={index === 0 ? "Expiry Date" : ""}
                      aria-label="Expiry Date"
                      showErrorText={false}
                      disabled={userFormState.actionOptions.disableAll}
                    />
                  </Grid.Col>

                  <Grid.Col
                    span={1}
                    style={{ display: "flex", alignItems: "flex-end" }}
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => {
                        apiKeysArray.remove(index);
                      }}
                      style={{
                        display:
                          userFormState.currentAction.toUpperCase() === "VIEW"
                            ? "none"
                            : "",
                      }}
                      disabled={userFormState.actionOptions.disableAll}
                    >
                      <IconTrash />
                    </ActionIcon>
                  </Grid.Col>
                </Grid>
              );
            })}
          </Stack>
        </div>

        {(Object.keys(errors).length > 0 || userFormState.error) && (
          <div style={styles.errors} id="errors">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormStateError errors={errors} />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormSubmitError error={userFormState.error} />
            </div>
          </div>
        )}

        <Grid align="stretch" gutter={"xs"} columns={24} mt="lg" mb={"lg"}>
          <>
            {(userFormState.actionOptions.allowedActions || []).map(
              (action: string) => {
                if (action === "save") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        fullWidth
                        type="submit"
                        color="teal"
                        aria-label="Save"
                        loading={userFormState.submitting}
                      >
                        Save
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "cancel") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        fullWidth
                        component={Link}
                        to={`/app/users?entityId=${entityId}`}
                        aria-label="Cancel"
                      >
                        Cancel
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "back") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        fullWidth
                        component={Link}
                        to={`/app/users?entityId=${entityId}`}
                        aria-label="Back"
                      >
                        Back
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "edit") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Edit"
                        fullWidth
                        onClick={() => {
                          userFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "EDIT",
                          });
                          userFormDispatch({
                            type: "ACTION-OPTIONS",
                            value: getActionOptions(
                              "edit",
                              dataset,
                              dataset,
                              applicationState
                            ),
                          });

                          setAudit({
                            status: dataset?.status,
                            authStatus: "UNAUTH",
                            usableStatus: dataset?.usableStatus,
                            makerId: applicationState.user.userId,
                            makerDate: new Date(),
                            modificationNo:
                              dataset?.authStatus === "UNAUTH"
                                ? dataset?.modificationNo
                                : (dataset?.modificationNo || 0) + 1,
                            checkerId: "",
                            checkerDate: undefined,
                          });
                        }}
                      >
                        Edit
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "auth") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Auth"
                        fullWidth
                        onClick={async () => {
                          userFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "AUTH",
                          });

                          try {
                            let payload = {
                              ...dataset,
                              entityId,
                              metaData: {
                                action: "auth",
                              },
                            };
                            await postUser(entityId, payload);
                            // setError("Form", "Unable to Submit");
                            userFormDispatch({
                              type: "CURRENT-ACTION",
                              value: "VIEW",
                            });

                            userFormDispatch({
                              type: "SET-SUCCESS",
                              value: "User Authorized Successfully",
                            });
                          } catch (error: any) {
                            // console.log("post catch", error);
                            userFormDispatch({
                              type: "SET-ERROR",
                              value:
                                error?.response?.data?.message ||
                                "Unable to Authorize User",
                            });
                          }
                        }}
                      >
                        Auth
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "delete") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Delete"
                        fullWidth
                        onClick={async () => {
                          userFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "DELETE",
                          });

                          try {
                            let payload = {
                              ...dataset,
                              entityId,
                              metaData: {
                                action: "delete",
                              },
                            };
                            await postUser(entityId, payload);
                            // setError("Form", "Unable to Submit");
                            userFormDispatch({
                              type: "CURRENT-ACTION",
                              value: "VIEW",
                            });

                            userFormDispatch({
                              type: "SET-SUCCESS",
                              value: "User Deleted Successfully",
                            });
                          } catch (error: any) {
                            // console.log("post catch", error);
                            userFormDispatch({
                              type: "SET-ERROR",
                              value:
                                error?.response?.data?.message ||
                                "Unable to Delete User",
                            });
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </Grid.Col>
                  );
                } else {
                  return null;
                }
              }
            )}
          </>
        </Grid>

        <Audit audit={audit} />
        {userFormState.successMessage && (
          <Success message={userFormState.successMessage} onOk={onOk} />
        )}
      </form>
    </UserCVMContext.Provider>
  );
}
